<template>
    <div v-if="hasChanges" class="subscription-footer" :class="{ 'subscription-footer--visible': hasChanges }">
        <div class="subscription-footer__content">
            <div class="subscription-footer__text">
                <h3 class="subscription-footer__title">
                    You've made <span class="subscription-footer__count">{{ changeCount }}</span> {{ changeCount === 1 ? 'change' : 'changes' }}
                </h3>
                <p class="subscription-footer__subtitle">
                    {{ subtitleText }}
                    <a href="#" @click.prevent="reset" v-if="showSomeoneElseLink">(Someone Else?)</a>
                </p>
            </div>
            <form :action="updateUrl" method="POST" @submit.prevent="confirmSubscriptions" class="subscription-footer__form">
                <p class="subscription-footer__label" v-if="showPasswordField && userEmail">
                    To update your subscriptions, please enter your password.
                </p>
                <p class="subscription-footer__label" v-else-if="showPasswordField">
                    Nice! All we need is a password next.
                </p>
                <div class="subscription-footer__input-group">
                    <template v-if="userEmail && !showPasswordField">
                        <button type="submit" class="subscription-footer__big-button" :disabled="isSubmitting">
                            {{ buttonText }}
                        </button>
                    </template>
                    <template v-else>
                        <input type="password"
                            ref="passwordInput"
                            v-model="password"
                            placeholder="Password"
                            minlength="7"
                            maxlength="255"
                            required
                            class="subscription-footer__input"
                            :disabled="isSubmitting"
                            v-if="showPasswordField"
                        />
                        <input type="email"
                            v-model="email"
                            placeholder="Email"
                            maxlength="255"
                            required
                            class="subscription-footer__input"
                            :disabled="isSubmitting"
                            v-else
                        />
                        <button type="submit" class="subscription-footer__button" :disabled="isSubmitting || !email.trim() || (showPasswordField && !password.trim())">
                            {{ buttonText }}
                        </button>
                    </template>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'Footer',
    props: {
        subscriptionChanges: {
            type: Array,
            default: () => [],
        },
        userEmail: {
            type: String,
            default: '',
        },
        updateUrl: {
            type: String,
            required: true,
        },
        isLoggedIn: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            email: this.userEmail,
            password: '',
            isSubmitting: false,
            signUpAttempted: false,
        };
    },
    computed: {
        hasChanges() {
            return this.subscriptionChanges.length > 0;
        },
        changeCount() {
            return this.subscriptionChanges.length;
        },
        showPasswordField() {
            return ! this.isLoggedIn && (this.userEmail || this.signUpAttempted);
        },
        buttonText() {
            if (this.isSubmitting) {
                return this.showPasswordField ? 'Confirming...' : 'Updating...';
            }

            return this.showPasswordField ? 'Update Subscriptions' : 'Confirm Subscriptions';
        },
        subtitleText() {
            const subscriptions = this.subscriptionChanges.filter((c) => c.action === 'subscribe').length;
            const unsubscriptions = this.subscriptionChanges.filter((c) => c.action === 'unsubscribe').length;

            if (this.userEmail || (this.email && this.signUpAttempted)) {
                return `You'll receive them at [${this.email}].`;
            } else if (subscriptions > 0 && unsubscriptions > 0) {
                return `You're subscribing to ${subscriptions} and unsubscribing from ${unsubscriptions} newsletters.`;
            } else if (subscriptions > 0) {
                return `You're subscribing to ${subscriptions} new ${subscriptions === 1 ? 'newsletter' : 'newsletters'}.`;
            } else if (unsubscriptions > 0) {
                return `You're unsubscribing from ${unsubscriptions} ${unsubscriptions === 1 ? 'newsletter' : 'newsletters'}.`;
            }

            return this.userEmail ? 'Confirm your subscription changes.' : 'Complete your changes by entering your email.';
        },
        showSomeoneElseLink() {
            // Only show for non-logged-in users who have entered an email to manage subscriptions
            return !this.isLoggedIn && (this.userEmail || (this.email && this.signUpAttempted));
        },
    },
    methods: {
        async confirmSubscriptions() {
            if (this.isSubmitting || ! this.hasChanges || this.email.trim().length === 0) {
                return;
            }

            // If password is required but user hasn't attempted sign up yet, show password field
            if (! this.isLoggedIn && ! this.signUpAttempted) {
                this.signUpAttempted = true;
                this.$nextTick(() => {
                    if (this.$refs.passwordInput) {
                        this.$refs.passwordInput.focus();
                    }
                });
                return;
            }

            if (this.showPasswordField && this.password.trim().length === 0) {
                return;
            }

            let isLoggedInNow = false;
            this.isSubmitting = true;
            
            try {
                const requestData = {
                    _token: document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
                    email: this.email,
                    changes: this.subscriptionChanges,
                };

                if (this.showPasswordField) {
                    requestData.password = this.password;
                }

                const response = await axios.post(this.updateUrl, requestData);
                isLoggedInNow = response.data?.logged_in ?? false;

                this.$emit('subscribe', {
                    email: this.email,
                    changes: this.subscriptionChanges,
                    result: response.data,
                });

                // Reload the page to show updated subscriptions
                if (! this.isLoggedIn) {
                    window.location = '/subscriptions?email=' + encodeURIComponent(this.email);
                }
            } catch (error) {
                let errorMessage = 'An error occurred while updating subscriptions.';
                let errorTitle = 'Update failed.';
                if (error.response) {
                    isLoggedInNow = error.response.data?.logged_in ?? false;
                    if (error.response.status === 419) {
                        errorTitle = 'Page expired.';
                        errorMessage = 'Please refresh and try again.';
                    } else if (error.response.status === 403) {
                        errorTitle = 'Authentication failed.';
                        errorMessage = error.response.data?.message || 'Invalid password. Please try again.';
                    } else {
                        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Network error: Unable to connect to server.';
                } else {
                    errorMessage = error.message || errorMessage;
                    console.error('Subscription error:', error);
                }

                this.$emit('subscribe-error', {
                    error: error,
                    message: errorMessage,
                    title: errorTitle,
                });
            } finally {
                this.isSubmitting = false;
                if (! this.isLoggedIn && isLoggedInNow) {
                    window.location.reload();
                }
            }
        },
        reset() {
            // if the user email came from the query string, reload it
            if (this.userEmail) {
                window.location = '/subscriptions';
            } else {
                this.email = '';
                this.password = '';
                this.signUpAttempted = false;
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
.subscription-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: black;
    z-index: 1000;
    transform: translateY(100%);
    transition: transform 0.3s ease-in-out;
    padding: 20px 0;
}

.subscription-footer--visible {
    transform: translateY(0);
}

.subscription-footer__content {
    max-width: 1140px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
}

.subscription-footer__text {
    flex: 1;
}

.subscription-footer__title {
    font-family: 'Oswald', sans-serif;
    font-weight: normal;
    font-size: 45px;
    color: #ef8125;
    margin: 0 0 8px 0;
    line-height: normal;
}

.subscription-footer__count {
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    color: white;
}

.subscription-footer__subtitle {
    font-family: 'Montserrat', sans-serif;
    font-weight: normal;
    font-size: 17px;
    color: white;
    margin: 0;
    line-height: normal;
}

.subscription-footer__form {
    flex-shrink: 0;
    margin-left: 40px;
}

.subscription-footer__input-group {
    display: flex;
    align-items: center;
}

.subscription-footer__label {
    font-family: 'Montserrat', sans-serif;
    font-weight: normal;
    font-size: 17px;
    color: white;
    line-height: normal;
}

.subscription-footer__input {
    width: 370px;
    height: 48px;
    background-color: white;
    border: none;
    padding: 0 18px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 18px;
    color: black;
    
    &::placeholder {
        color: rgba(0, 0, 0, 0.5);
    }
    
    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px #ef8125;
    }
    
    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
}

.subscription-footer__button,
.subscription-footer__big-button {
    width: 220px;
    height: 48px;
    background-color: #ef8125;
    border: none;
    color: white;
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    font-size: 20px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 0.3s ease;
    
    &:hover:not(:disabled) {
        background-color: #d16d1e;
    }
    
    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
}

.subscription-footer__big-button {
    width: 370px;
}

@media (max-width: 1024px) {
    .subscription-footer__content {
        flex-direction: column;
        gap: 20px;
        text-align: center;
    }
    
    .subscription-footer__form {
        margin-left: 0;
    }
    
    .subscription-footer__title {
        font-size: 36px;
    }
}

@media (max-width: 768px) {
    .subscription-footer__content {
        padding: 0 16px;
    }
    
    .subscription-footer__input-group {
        flex-direction: column;
        gap: 12px;
        width: 100%;
    }
    
    .subscription-footer__input {
        width: 100%;
    }
    
    .subscription-footer__button,
    .subscription-footer__big-button {
        width: 100%;
        padding: 0 20px;
    }
    
    .subscription-footer__title {
        font-size: 28px;
    }
    
    .subscription-footer__subtitle {
        font-size: 15px;
    }
}
</style>